import { useState, useRef, useCallback, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Camera as CameraIcon, Image, Upload, X, Check, RefreshCw, Download, FileText } from 'lucide-react';

function Camera() {
  const { API } = useContext(AppContext);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [isStreaming, setIsStreaming] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedImages, setCapturedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedReport, setSelectedReport] = useState('');
  const [description, setDescription] = useState('');

  const reportTypes = [
    { value: 'production', label: 'Production Report' },
    { value: 'quality', label: 'Quality Control' },
    { value: 'dispatch', label: 'Dispatch Record' },
    { value: 'inventory', label: 'Inventory Check' },
    { value: 'maintenance', label: 'Maintenance Log' },
    { value: 'other', label: 'Other Document' },
  ];

  const startCamera = useCallback(async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Unable to access camera. Please ensure camera permissions are granted.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsStreaming(false);
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(imageData);
      stopCamera();
    }
  }, [stopCamera]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const savePhoto = async () => {
    if (!capturedImage || !selectedReport) {
      setError('Please select a report type');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/photos`, {
        image: capturedImage,
        reportType: selectedReport,
        description: description,
        timestamp: new Date().toISOString()
      }, {
        
      });

      setCapturedImages(prev => [{
        id: response.data?.id || Date.now(),
        image: capturedImage,
        reportType: selectedReport,
        description: description,
        timestamp: new Date().toISOString()
      }, ...prev]);

      setCapturedImage(null);
      setDescription('');
      setSelectedReport('');
    } catch (err) {
      console.error('Upload error:', err);
      // For demo, still save locally
      setCapturedImages(prev => [{
        id: Date.now(),
        image: capturedImage,
        reportType: selectedReport,
        description: description,
        timestamp: new Date().toISOString()
      }, ...prev]);
      
      setCapturedImage(null);
      setDescription('');
      setSelectedReport('');
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = (id) => {
    setCapturedImages(prev => prev.filter(img => img.id !== id));
  };

  return (
    <div className="space-y-6" data-testid="camera-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Document Camera</h1>
        <p className="text-gray-600 mt-1">Capture and upload photos of reports and documents</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Camera Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
              <CameraIcon className="text-blue-600" size={20} />
              <span>Camera Capture</span>
            </h3>
          </div>

          <div className="p-4 space-y-4">
            {/* Camera Preview / Captured Image */}
            <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
              {capturedImage ? (
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="w-full h-full object-contain"
                  data-testid="captured-image"
                />
              ) : isStreaming ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="camera-stream"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                  <CameraIcon size={48} />
                  <p className="mt-2 text-sm">Click "Start Camera" to begin</p>
                </div>
              )}
            </div>
            
            <canvas ref={canvasRef} className="hidden" />

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="camera-error">
                {error}
              </div>
            )}

            {/* Camera Controls */}
            <div className="flex flex-wrap gap-2">
              {!capturedImage && !isStreaming && (
                <>
                  <button
                    onClick={startCamera}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition flex items-center justify-center space-x-2"
                    data-testid="start-camera-btn"
                  >
                    <CameraIcon size={18} />
                    <span>Start Camera</span>
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition flex items-center justify-center space-x-2"
                    data-testid="upload-file-btn"
                  >
                    <Upload size={18} />
                    <span>Upload File</span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                </>
              )}

              {isStreaming && (
                <>
                  <button
                    onClick={capturePhoto}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-lg transition flex items-center justify-center space-x-2"
                    data-testid="capture-btn"
                  >
                    <CameraIcon size={18} />
                    <span>Capture Photo</span>
                  </button>
                  <button
                    onClick={stopCamera}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition"
                    data-testid="stop-camera-btn"
                  >
                    <X size={18} />
                  </button>
                </>
              )}

              {capturedImage && (
                <>
                  <button
                    onClick={retakePhoto}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition flex items-center justify-center space-x-2"
                    data-testid="retake-btn"
                  >
                    <RefreshCw size={18} />
                    <span>Retake</span>
                  </button>
                </>
              )}
            </div>

            {/* Save Form - Only show when image is captured */}
            {capturedImage && (
              <div className="space-y-3 pt-3 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report Type *</label>
                  <select
                    value={selectedReport}
                    onChange={(e) => setSelectedReport(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    data-testid="report-type-select"
                  >
                    <option value="">Select report type...</option>
                    {reportTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add notes about this document..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                    data-testid="description-input"
                  />
                </div>

                <button
                  onClick={savePhoto}
                  disabled={uploading || !selectedReport}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-photo-btn"
                >
                  {uploading ? (
                    <>
                      <RefreshCw size={18} className="animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      <span>Save Photo</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Captured Images Gallery */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
              <Image className="text-blue-600" size={20} />
              <span>Captured Documents ({capturedImages.length})</span>
            </h3>
          </div>

          <div className="p-4">
            {capturedImages.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Image size={48} className="mx-auto mb-3 opacity-50" />
                <p>No documents captured yet</p>
                <p className="text-sm mt-1">Use the camera to capture report photos</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {capturedImages.map((img) => (
                  <div
                    key={img.id}
                    className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300 transition"
                    data-testid={`captured-doc-${img.id}`}
                  >
                    <img
                      src={img.image}
                      alt="Captured document"
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <FileText size={14} className="text-blue-600" />
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {reportTypes.find(r => r.value === img.reportType)?.label || img.reportType}
                        </span>
                      </div>
                      {img.description && (
                        <p className="text-sm text-gray-600 mt-1 truncate">{img.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(img.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteImage(img.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition"
                      data-testid={`delete-doc-${img.id}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <CameraIcon className="text-blue-600 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-blue-900">Tips for better document capture</h4>
            <ul className="text-sm text-blue-700 mt-1 space-y-1">
              <li>Ensure good lighting - avoid shadows on the document</li>
              <li>Keep the camera steady and the document flat</li>
              <li>Make sure all text is visible and in focus</li>
              <li>For best results, capture one page at a time</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Camera;
